/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Action, NgxsOnInit, Selector, SelectorOptions, State, StateContext, Store } from '@ngxs/store';
import { catchError, concatMap, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import {
    AcceptedForgotPasswordRequest,
    BeginConfirmAccount,
    BeginForceResetPassword,
    BeginLogout,
    BeginPasswordLogin,
    BeginTokenRefresh,
    CheckConfirmationKey,
    CheckConfirmationKeyFailed,
    CheckConfirmationKeySuccess,
    CheckResetKey,
    ConfirmAccountFailed,
    ConfirmAccountSuccess,
    DisclaimerSeen,
    EndForceResetPassword,
    EndInitialAuthentication,
    InitForgotPasswordRequest,
    LoadAuthConfig,
    LoggedOut,
    LoginFailed,
    LoginSuccessful,
    NavigateToRequestedRoute,
    PrepareToLogin,
    RedirectToLogin,
    RequestUserInfo,
    ResetForgottenPassword,
    ResetForgottenPasswordFailure,
    ResetForgottenPasswordSuccess,
    ResetPassword,
    ResetPasswordFailed,
    ResetPasswordSuccess,
    SetAuthConfigOptions,
    SetRequestedRoute,
    SetUserInfo,
    StoreRefreshToken,
    TokenRefreshFailed,
    TokenRefreshSuccessful,
    UpdatePasswordComplexityUI
} from './authState.actions';
import { AuthMethods, AuthStateModel, LoginPayload, LoginResponse, RequestedRoute } from './authState.model';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { Navigate } from '@ngxs/router-plugin';
import { AppConfigState } from '../../state/appConfig/app-config.state';
import { Inject, Injectable } from '@angular/core';
import { patch } from '@ngxs/store/operators';
import { RestService } from '../../../modules/shared/services/rest.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ThrowError } from '../../state/notification/notification.actions';
import { ResetPasswordService } from '../services/reset-password.service';
import { Router } from '@angular/router';
import { STORAGE_ENGINE, StorageEngine } from '@ngxs/storage-plugin';
import { AutoLoginConfig } from '../../../types';

@State<AuthStateModel>({
    name: 'auth',
    defaults: {
        loaded: false,
        activeMethod: 'none',
        methods: {},
        token: '',
        refreshToken: '',
        validated: false,
        user: {
            id: null,
            username: '',
            roles: []
        },
        needsDisclaimer: true,
        remember: false,
        authenticationInProgress: true,
        isStartingUp: true,
        isInitialVisit: true,
        requestedRoute: null,
        forgotPasswordRequestState: 'none',
        forcePasswordResetInProgress: false,
        excludedRoutes: [
            '/auth/forgot',
            '/auth/reset',
            '/auth/confirm',
            '/auth/disclaimer',
        ]
    }
})
@Injectable()
@SelectorOptions({injectContainerState: false})
export class AuthState implements NgxsOnInit {
    constructor(
        private logger: LoggerService,
        private jwtService: JwtHelperService,
        private reset: ResetPasswordService,
        private rest: RestService,
        private store: Store,
        private router: Router,
        @Inject(STORAGE_ENGINE) private storage: StorageEngine,
    ) {}

    ngxsOnInit(ctx: StateContext<AuthStateModel>) {
        const appConfigLoaded$ = this.store.select(AppConfigState.loaded).pipe(filter(Boolean));
        appConfigLoaded$
            .pipe(
                tap(() => setTimeout(() => ctx.dispatch(new LoadAuthConfig()), 0)),
                take(1)
            )
            .subscribe();
    }

    @Action(LoadAuthConfig)
    loadAuthConfig(ctx: StateContext<AuthStateModel>) {
        return this.rest.apiGet('auth/config').pipe(
            tap((authConfig: AuthMethods) => {
                ctx.setState(patch<AuthStateModel>({ methods: authConfig, loaded: true }));
            })
        );
    }

    @Action(EndInitialAuthentication)
    endInitialAuthentication({ setState }: StateContext<AuthStateModel>) {
        setState(patch({ authenticationInProgress: false, isStartingUp: false }));
    }

    @Action(SetAuthConfigOptions)
    setAuthConfigOptions({ setState }: StateContext<AuthStateModel>, action: SetAuthConfigOptions) {
        // if the disclaimer is not enabled, we can never need to display it
        if (!action.payload.enableDisclaimer) {
            setState(patch({ needsDisclaimer: false }));
        }
    }

    @Action(RequestUserInfo)
    requestUserInfo() {
    }

    @Action(SetUserInfo)
    setUserInfo({ setState }: StateContext<AuthStateModel>, action: SetUserInfo) {
        if (action.payload.success) {
            setState(
                patch({
                    user: patch({
                        id: action.payload.user.id,
                        username: action.payload.user.username,
                        authenticator: action.payload.user.authenticator,
                        roles: action.payload.user.roles
                    }),
                    validated: true,
                    isInitialVisit: false,
                    authenticationInProgress: false
                })
            );
        } else {
            setState(
                patch({
                    user: patch({
                        id: -1,
                        username: '',
                        roles: [] as string[]
                    }),
                    validated: false,
                    authenticationInProgress: false
                })
            );
        }
    }

    @Action(BeginTokenRefresh)
    beginTokenRefresh({ setState }: StateContext<AuthStateModel>) {
        setState(patch({ authenticationInProgress: true }));
    }

    @Action(TokenRefreshSuccessful)
    tokenRefreshSuccessful(
        { dispatch, getState, setState }: StateContext<AuthStateModel>,
        action: TokenRefreshSuccessful
    ) {
        setState(
            patch<AuthStateModel>({
                token: action.payload.token,
                refreshToken: action.payload.refreshToken,
                validated: true,
                activeMethod: 'password',
                authenticationInProgress: false
            })
        );
        return setTimeout(() => dispatch(new RequestUserInfo()), 0);
    }

    @Action(TokenRefreshFailed)
    tokenRefreshFailed({ setState }: StateContext<AuthStateModel>, action: TokenRefreshFailed) {
        this.logger.warn(`Token refresh failed. Reason: ${ action.reason }`);
        setState(patch({ token: '', refreshToken: '', validated: false }));
        setState(patch({ authenticationInProgress: false }));
    }

    @Action(PrepareToLogin)
    prepareToLogin({ dispatch, setState }: StateContext<AuthStateModel>, action: PrepareToLogin) {
        // in order for the 'split' storage engine proxy to work, we have to set the value of
        // 'remember' directly in storage before changing the state.
        // We are NOT saving it via the storage plugin

        // we also need to make sure we remove it after setting the state in the logout action
        this.storage.setItem('auth.remember', action.payload.remember);
        setState(
            patch({
                authenticationInProgress: true,
                activeMethod: action.payload.method,
                remember: action.payload.remember
            })
        );
    }

    @Action(BeginPasswordLogin)
    beginPasswordLogin({ setState }: StateContext<AuthStateModel>/* , action: BeginPasswordLogin */) {
        setState(patch({ authenticationInProgress: true }));
    }

    @Action(LoginSuccessful)
    loginSuccessful({ dispatch, getState, setState }: StateContext<AuthStateModel>, action: LoginSuccessful) {
        const state = getState();
        if (state.requestedRoute) {
            dispatch(new NavigateToRequestedRoute());
        } else {
            dispatch(new Navigate([ '/' ]));
        }
        const stateChanges = {
            validated: true,
            authenticationInProgress: false,
            isStartingUp: false,
        };
        setState(
            patch({
                ...stateChanges,
                ...action.payload
            })
        );
        dispatch(new StoreRefreshToken(action.payload.refreshToken));
    }

    @Action(LoginFailed)
    loginFailed({ setState }: StateContext<AuthStateModel>) {
        setState(
            patch({
                authenticationInProgress: false,
                isStartingUp: false,
            })
        );
    }

    @Action(BeginLogout)
    beginLogout({ setState }: StateContext<AuthStateModel>) {
        const emptyRoles: string[] = [];
        setState(patch({
            authenticationInProgress: true,
            token: '',
            refreshToken: '',
            validated: false,
            activeMethod: 'none',
            user: patch({
                id: null,
                username: '',
                authenticator: null,
                roles: emptyRoles
            })
        }));
        this.storage.setItem('auth.remember', false);
    }

    @Action(LoggedOut)
    loggedOut({ dispatch, setState }: StateContext<AuthStateModel>, action: LoggedOut) {
        setState(patch({ authenticationInProgress: false, isStartingUp: false }));
        return action.redirect
            ? dispatch(new Navigate([ '/' ], null, { queryParamsHandling: '' }))
            : true;
    }

    @Action(SetRequestedRoute)
    setRequestedRoute({ setState }: StateContext<AuthStateModel>, action: SetRequestedRoute) {
        setState(patch({ requestedRoute: action.requestedRoute }));
    }

    @Action(RedirectToLogin)
    redirectToLogin({ dispatch, setState }: StateContext<AuthStateModel>, action: RedirectToLogin) {
        setState(patch({authenticationInProgress: false}));
        if (this.shouldOverwriteRequestedRoute(action.route)) {
            setState(
                patch({
                    requestedRoute: action.route
                })
            );
        }
        setTimeout(() => dispatch(new Navigate([ '/auth/login' ], null, { queryParamsHandling: 'preserve' })), 0);
    }

    @Action(NavigateToRequestedRoute)
    navigateToRequestedRoute(
        { dispatch, getState, setState }: StateContext<AuthStateModel>,
        action: NavigateToRequestedRoute
    ) {
        const requestedRoute = getState().requestedRoute || action.fallbackRoute;
        if (requestedRoute) {
            setState(patch({ requestedRoute: null }));
            setTimeout(() => dispatch(new Navigate([ requestedRoute.url ], requestedRoute.queryParams)), 0);
        }
    }

    @Action(DisclaimerSeen)
    disclaimerSeen({ dispatch, getState, setState }: StateContext<AuthStateModel>) {
        const state = getState();
        const autoLogin = this.store.selectSnapshot(AppConfigState.autoLogin);
        const isInitialVisit = state.isInitialVisit;
        const isNoActiveMethod = state.activeMethod === 'none';
        const fullPath = this.router.url.split(/[?#]/)[0];
        // disclaimer is not authenticated, but we don't want to force logout either
        const excludedRoutes = state.excludedRoutes.filter(r => r !== '/auth/disclaimer');
        const skipAuthByRoute = excludedRoutes.includes(fullPath);
        setState(patch({ needsDisclaimer: false, isInitialVisit: false }));
        if (skipAuthByRoute) {
            setTimeout(() => dispatch(new BeginLogout(false)), 0);
            return;
        }
        if (isInitialVisit
            && isNoActiveMethod
            && autoLogin.enabled
            && autoLogin.guestuser
            && autoLogin.guestpassword
        ) {
            const payload: LoginPayload = {
                credentials: {
                    username: autoLogin.guestuser,
                    password: autoLogin.guestpassword
                }
            };
            setTimeout(() => dispatch(new PrepareToLogin({ method: 'password', remember: false })), 0);
            setTimeout(() => dispatch(new BeginPasswordLogin(payload)), 0);
        } else {
            setTimeout(() => dispatch(new NavigateToRequestedRoute({ url: '/' })), 0);
        }
    }

    @Action(InitForgotPasswordRequest)
    initForgotPasswordRequest(
        { dispatch, getState, setState }: StateContext<AuthStateModel>,
        action: InitForgotPasswordRequest
    ) {
        setState(patch({ forgotPasswordRequestState: 'started' }));
        const data = { 'identifier': action.identifier };
        return this.rest.apiPost('auth/password/forgot', data, null, { skipErrorHandler: false }).pipe(
            concatMap(() => dispatch(new AcceptedForgotPasswordRequest())),
            catchError((err) => dispatch(new ThrowError(err)))
        );
    }

    @Action(AcceptedForgotPasswordRequest)
    acceptedForgotPasswordRequest({ setState }: StateContext<AuthStateModel>) {
        setState(patch({ forgotPasswordRequestState: 'complete' }));
    }

    @Action(CheckResetKey)
    checkResetKey({ dispatch }: StateContext<AuthStateModel>, action: CheckResetKey) {
        return this.reset.verifyResetKey$(action.resetKey)
            .pipe(
                mergeMap((result) => {
                    const params = { key: action.resetKey, valid: !(result === false) };
                    return dispatch(new Navigate([ '/auth/reset' ], params));
                })
            );
    }

    @Action(ResetForgottenPassword)
    resetForgottenPassword({ dispatch }: StateContext<AuthStateModel>, action: ResetForgottenPassword) {
        return this.rest.apiPost<LoginResponse>(
            'auth/password/forgot/reset',
            action.payload, {}, { skipErrorHandler: true }
        ).pipe(
            take(1),
            tap(result => setTimeout(() => dispatch(new LoginSuccessful(result)), 0)),
            tap(() => setTimeout(() => dispatch(new ResetForgottenPasswordSuccess()), 50)),
            catchError((err) => dispatch(new ResetForgottenPasswordFailure(err.message)))
        );
    }

    @Action(ResetPassword)
    resetPassword({ dispatch }: StateContext<AuthStateModel>, action: ResetPassword) {
        return this.reset.resetPassword$(action.payload)
            .pipe(
                take(1),
                switchMap((result) => (result.success)
                    ? dispatch([ new LoginSuccessful(result), new ResetPasswordSuccess() ])
                    : dispatch(new ResetPasswordFailed(result.message)))
            );
    }

    @Action(ResetPasswordSuccess)
    resetPasswordSuccess() {}

    @Action(ResetPasswordFailed)
    resetPasswordFailed() {}

    @Action(UpdatePasswordComplexityUI)
    updatePasswordComplexityUI() {}

    @Action(CheckConfirmationKey)
    checkConfirmationKey({ dispatch }: StateContext<AuthStateModel>, action: CheckConfirmationKey) {
        return this.reset.verifyConfirmationKey$(action.confirmationKey)
            .pipe(
                // mergeMap((result) => {
                //     const params = { key: action.confirmationKey, valid: !(result === false) };
                //     return dispatch(new Navigate([ '/auth/confirm' ], params));
                // })
                tap((result) => result
                    ? setTimeout(() => dispatch(new CheckConfirmationKeySuccess()), 0)
                    : setTimeout(() => dispatch(new CheckConfirmationKeyFailed()), 0)),
            );
    }

    @Action(CheckConfirmationKeyFailed)
    checkConfirmationKeyFailed({ setState }: StateContext<AuthStateModel>) {
        setState(patch({ authenticationInProgress: false }));
    }

    @Action(BeginConfirmAccount)
    beginConfirmAccount({ dispatch }: StateContext<AuthStateModel>, action: BeginConfirmAccount) {
        setTimeout(() => dispatch(new PrepareToLogin({method: 'password', remember: false})), 0);
        return this.reset.confirmAccount$(action.payload).pipe(
            take(1),
            switchMap((result) => result.success
                ? dispatch([ new LoginSuccessful(result), new ConfirmAccountSuccess() ])
                : dispatch(new ConfirmAccountFailed(result.message)))
        );
    }

    @Action(BeginForceResetPassword)
    beginForceResetPassword({ setState }: StateContext<AuthStateModel>) {
        setState(patch({ forcePasswordResetInProgress: true }));
    }

    @Action(EndForceResetPassword)
    endForceResetPassword({ setState }: StateContext<AuthStateModel>) {
        setState(patch({ forcePasswordResetInProgress: false }));
    }

    private shouldOverwriteRequestedRoute(newRequestedRoute: RequestedRoute) {
        const url = newRequestedRoute?.url || null;
        const q = newRequestedRoute?.queryParams;

        const ignoreNewUrl = [ null, undefined, '' ].includes(url);

        const ignoreParams = q ? Object.keys(q)
            .filter((p) => [ 'code', 'state', 'error' ].includes(p)).length > 0 : true;

        return !(ignoreNewUrl || (ignoreParams && url === '/'));
    }

    @Selector([AuthState])
    static loaded(state: AuthStateModel): boolean {
        return state.loaded;
    }

    @Selector([AuthState])
    static authMethods(state: AuthStateModel): string[] {
        return Object.keys(state.methods);
    }

    @Selector([AuthState])
    static methodConfig(state: AuthStateModel): (string) => any {
        return (method: string) => state.methods[method].config;
    }

    @Selector([AuthState])
    static loginMethods(state: AuthStateModel): string[] {
        const arr = Object.keys(state.methods).map((k) => state.methods[k].type);
        // make unique
        return [ ...new Set(arr) ];
    }

    @Selector([AuthState])
    static token(state: AuthStateModel) {
        return state.token;
    }

    @Selector([AuthState])
    static validated(state: AuthStateModel) {
        return state.validated;
    }

    @Selector([AuthState])
    static refreshToken(state: AuthStateModel) {
        return state.refreshToken;
    }

    @Selector([AuthState])
    static remember(state: AuthStateModel) {
        return state.remember;
    }

    @Selector([AuthState])
    static user(state: AuthStateModel) {
        return state.user;
    }

    @Selector([AuthState, AppConfigState.enableDisclaimer])
    static needsDisclaimer(state: AuthStateModel, enableDisclaimer: boolean) {
        return state.needsDisclaimer && enableDisclaimer;
    }

    @Selector([AuthState])
    static authenticationInProgress(state: AuthStateModel) {
        return state.authenticationInProgress;
    }

    @Selector([AuthState])
    static isStartingUp(state: AuthStateModel) {
        return state.isStartingUp;
    }

    @Selector([AuthState])
    static requestedRoute(state: AuthStateModel) {
        return state.requestedRoute;
    }

    @Selector([AuthState])
    static activeMethod(state: AuthStateModel) {
        return state.activeMethod;
    }

    @Selector([AuthState])
    static forgotPasswordRequestState(state: AuthStateModel) {
        return state.forgotPasswordRequestState;
    }

    @Selector([AuthState])
    static forcePasswordResetInProgress(state: AuthStateModel) {
        return state.forcePasswordResetInProgress;
    }

    @Selector([AuthState])
    static excludedRoutes(state: AuthStateModel) {
        return state.excludedRoutes;
    }

    @Selector([AuthState, AppConfigState.autoLogin])
    static isDefaultUser(state: AuthStateModel, autoLogin: AutoLoginConfig) {
        return autoLogin && state.user.username === autoLogin.guestuser;
    }
}
